import { createContext, ReactNode, useContext, useMemo, useState } from "react";
import Cookies from "js-cookie";
import { getCookieDomainSafe } from "../utils/authStorage";

// Same cookie key + attributes as the ACT SI Console, so the preference is
// shared across ACT apps on the same domain.
export const DARK_MODE_KEY = "darkMode";

interface ThemeContextValue {
  darkMode: boolean;
  toggleDarkMode: () => void;
  setDarkMode: (isDark: boolean) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

function persistDarkMode(isDark: boolean): void {
  Cookies.set(DARK_MODE_KEY, String(isDark), {
    domain: getCookieDomainSafe(),
    secure: false,
    sameSite: "Lax",
  });
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [darkMode, setDarkModeState] = useState(() => Cookies.get(DARK_MODE_KEY) === "true");

  const value = useMemo(() => {
    const setDarkMode = (isDark: boolean) => {
      persistDarkMode(isDark);
      setDarkModeState(isDark);
    };
    return {
      darkMode,
      setDarkMode,
      toggleDarkMode: () => setDarkMode(!darkMode),
    };
  }, [darkMode]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useThemeContext(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useThemeContext must be used within a ThemeProvider");
  }
  return context;
}
